// 22.01.19 - This is used to map the names in api.tsx to show tooltips.
import { ICase } from "./api";

// Filter all keys that are arrays. Will return i.e. pos
type AllArrayKeys<T> = { [K in keyof T]: T[K] extends any[] ? K : never }[keyof T];
// Filter all keys that are not arrays. i.e. caseno.
type AllNonArrayKeys<T> = { [K in keyof T]: T[K] extends any[] ? never : K }[keyof T];

// This takes a type like `Foo[]` and turns it into `Foo`.
type RemoveArray<T> = T extends Array<infer U> ? U : T;

// This complicated type takes a type like `ICase` and turns it into another type,
// where all the fields' types are strings, except for fields who themselves are complicated
// object types(like IQMU, IPo, etc). For those, it does the same thing
// This means that the tooltip map above is a map of all possible fields in our `ICase` type,
// even nested ones, with every field being a string.
type TooltipType<T> = { [K in AllArrayKeys<Required<T>>]?: TooltipType<RemoveArray<Required<T>[K]>> } & { [U in AllNonArrayKeys<Required<T>>]?: string };

// Redux-form gives nested fields names like `shipment[0].foo` or
// `pos.bar`. We want split these into ["shipment", "foo"] and ["pos", "bar"]
// so that we can use these keys to look up the field in the `tooltips` object below
// to find the appropriate tooltip for each field
function splitFieldName(fieldName: string) {
  return fieldName.split(/[\[\]0-9.]+/);
}

export function lookupTooltip(fieldName: string): string | undefined {
  const splits = splitFieldName(fieldName);
  // We abandon type safety here but the alternative type-safe solution is not worth it in this case
  let obj: any | undefined = tooltips;
  for (const split of splits) {
    if (obj) {
      obj = obj[split];
    }
  }
  return obj;
}

// Tooltips for the fields
const tooltips: TooltipType<ICase> = {
  submittingOnBehalfOf: "Fill in if you are submitting on behalf of someone else",
  contactPerson: "Name of person to contact for further questions on the case",
  phone: "Phone number to contact person",
  email: "Email to contact person",
  caseType: "Damage or missing items during transit - choose Complaint and Claim. Damage or missing items in storage - choose Claim. For any other issues choose Complaint",
  countriesAffected: "Select the country(ies) affected by the issue",
  descriptionOfTheIssue: "Describe the issue with as many details as possible",
  dateIssueNoticed: "The date when the issue was first discovered",
  issueType: "Choose the process/type of issue the case is related to",
  poNo: "Insert the 8 digit PO number. In case of more POs separate with comma",
  shipmentNo: "Insert the 8 digit shipment number",
  expectedLossValue: "The total USD value of goods lost or damaged",
  comments: "Any comments or questions to the case can be added here",
  pos: {
    poItemNo: "Choose the item number(s) relevant to the case",
    assetNumbers: "For cases related to assets insert the relevant asset number(s)",
    batchNo: "For cases related to commodities with batch numbers insert the relevant batch number(s)",
    serialNo: "For cases related to commodities with serial numbers insert the relevant serial number(s)"
  },
  shipments: {
    description: "Describe the loss, including number of items damaged/missing",
    sealIntact: "Was the container seal properly locked",
    tampering: "Is there visible sign of theft, is there signs of the container seal having been replaced",
    containerNo: "Insert the container identification numbers, if multiple separate with comma",
    packingIntact: "Were all boxes sealed, and with original tape/straps",
    dateReceivedInCountry: "Insert date of when the goods arrived in country",
    sealNo: "Numbers of the seals attached to the container(s)",
    customsClearanceDate: "Insert date of when customs clearance was completed",
    quantity: "Total number of damage/missing/shortshipped supplies",
    BLDate: "Date the B/L, AWB, CMR has been issued",
    meansOfTransport: "Air, sea, road",
    quantityLostPerMaterialLevel: "Enter physcial quantities damaged, missing. (Material data fields including items at material level and weight. Could be multiple lines)",
    claimsValueFreight: "Enter applicable freight values: actual freight value, estimated freight value, proportional freight value or replacement value. (Transport by another means, surface becomes air)",
    PSB: "For losses the PSB process should be followed see further guidelines in UNICEF Financial and Administrative Policy 7: Property Plant and Equipment",
    psbcomments: "Please describe reason why case has not been submitted to PSB",
    recoveryValue: "Value of estimated recovery against liable 3rd party",
    nameOfThirdParty: "Insert name of liable 3rd party (Forwarder, carrier, warehouse owner)",
    amountOfRecoveryObtained: "USD value",
    brokerReference: "Broker case reference number",
    claimDesignation: "Could be small claim, large claim or other",
    presentedForSettlement: "Insert date",
    claimClosed: "Insert date",
    settlementAgreed: "Insert date"
  }
};
